import { createRootRoute, createRoute, createRouter, redirect, Outlet } from "@tanstack/react-router";
import { Shell } from "./app/Shell";
import { PortalProvider } from "./portal/state";
import { getSession as portalGetSession } from "./portal/portalSession";
import { SignIn } from "./portal/SignIn";
import { PortalShell } from "./portal/PortalShell";
import { PortalSection } from "./portal/PortalSection";
import { PortalHome } from "./portal/PortalHome";
import { PortalCategory } from "./portal/PortalCategory";
import { PortalArticle } from "./portal/PortalArticle";
import { PortalAsk } from "./portal/PortalAsk";
import { PortalOnboarding } from "./portal/PortalOnboarding";
import { PortalTickets } from "./portal/PortalTickets";
import { PortalTicket } from "./portal/PortalTicket";
import { PortalNewTicket } from "./portal/PortalNewTicket";
import { PortalProjects } from "./portal/PortalProjects";
import { PortalProject } from "./portal/PortalProject";
import { Dashboard } from "./screens/Dashboard";
import { Inbox } from "./screens/Inbox";
import { Archive } from "./screens/Archive";
import { Clients, Client360 } from "./screens/Clients";
import { Person } from "./screens/Person";
import { Team } from "./screens/Team";
import { Board } from "./screens/Board";
import { Knowledge } from "./screens/Knowledge";
import { KbArticle } from "./screens/KbArticle";
import { Projects } from "./screens/Projects";
import { ProjectDetail } from "./screens/ProjectDetail";
import { Incidents } from "./screens/Incidents";
import { StatusPage } from "./screens/StatusPage";
import { Vendors } from "./screens/Vendors";
import { Mailboxes } from "./screens/Mailboxes";
import { Automations } from "./screens/Automations";
import { Schedule } from "./screens/Schedule";
import { Hours } from "./screens/Hours";
import { Permissions } from "./screens/Permissions";
import { Audit } from "./screens/Audit";
import { Retention } from "./screens/Retention";
import { TrustedVendors } from "./screens/TrustedVendors";
import { Updates } from "./screens/Updates";
import { Phone } from "./screens/Phone";
import { Themes } from "./screens/Themes";
import { Deflect } from "./screens/Deflect";
import { OnCall } from "./screens/OnCall";
import { AdminPreview } from "./screens/AdminPreview";
import { Flows } from "./screens/Flows";
import { Macros } from "./screens/Macros";
import { Reports } from "./screens/Reports";
import { Parity } from "./screens/Parity";
import { Templates } from "./screens/Templates";
import { Attention } from "./screens/Attention";
import { MlsOps } from "./screens/MlsOps";
import { FeedHealth } from "./screens/FeedHealth";
import { Duplicates } from "./screens/Duplicates";
import { ContentStudio } from "./screens/ContentStudio";
import { Scaffold } from "./screens/common";

// The root is a bare passthrough so two independent layouts can hang off it: the
// staff app (green Shell chrome) and the fully-gated customer portal (brand
// chrome). Everything staff-side lives under `staffLayout`; the portal under
// `/portal`.
const rootRoute = createRootRoute({ component: () => <Outlet /> });
const staffLayout = createRoute({ getParentRoute: () => rootRoute, id: "staff", component: Shell });

const staffRoutes = staffLayout.addChildren([
  createRoute({ getParentRoute: () => staffLayout, path: "/", component: Dashboard }),
  // The open ticket lives in the PATH (/tickets/<id>) so an agent can copy the
  // address bar and share a direct link; /inbox is the queue landing. Both render
  // the same screen; the queue filter rides in ?view= so it survives the hop
  // between the two routes (and is itself shareable).
  createRoute({
    getParentRoute: () => staffLayout,
    path: "/inbox",
    component: Inbox,
    // `ticket` is accepted only so a legacy /inbox?ticket=<id> link (bookmarks,
    // history, older deep-links) can be redirected to the canonical /tickets/<id>.
    validateSearch: (s: Record<string, unknown>): { view?: string; ticket?: string } => ({
      ...(typeof s.view === "string" ? { view: s.view } : {}),
      ...(typeof s.ticket === "string" ? { ticket: s.ticket } : {}),
    }),
  }),
  createRoute({
    getParentRoute: () => staffLayout,
    path: "/tickets/$ticketId",
    component: Inbox,
    validateSearch: (s: Record<string, unknown>): { view?: string } => (typeof s.view === "string" ? { view: s.view } : {}),
  }),
  createRoute({ getParentRoute: () => staffLayout, path: "/clients", component: Clients }),
  createRoute({ getParentRoute: () => staffLayout, path: "/clients/$clientId", component: Client360 }),
  createRoute({ getParentRoute: () => staffLayout, path: "/person/$personId", component: Person }),
  createRoute({ getParentRoute: () => staffLayout, path: "/team", component: Team }),
  createRoute({ getParentRoute: () => staffLayout, path: "/board", component: Board }),
  createRoute({ getParentRoute: () => staffLayout, path: "/kb", component: Knowledge }),
  createRoute({ getParentRoute: () => staffLayout, path: "/kb/$articleId", component: KbArticle }),
  createRoute({ getParentRoute: () => staffLayout, path: "/projects", component: Projects }),
  createRoute({ getParentRoute: () => staffLayout, path: "/projects/$projectId", component: ProjectDetail }),
  createRoute({ getParentRoute: () => staffLayout, path: "/incidents", component: Incidents }),
  createRoute({ getParentRoute: () => staffLayout, path: "/status/$clientId", component: StatusPage }),
  createRoute({ getParentRoute: () => staffLayout, path: "/vendors", component: Vendors }),
  createRoute({ getParentRoute: () => staffLayout, path: "/phone", component: Phone }),
  createRoute({ getParentRoute: () => staffLayout, path: "/themes", component: Themes }),
  createRoute({ getParentRoute: () => staffLayout, path: "/updates", component: Updates }),
  createRoute({ getParentRoute: () => staffLayout, path: "/deflect", component: Deflect }),
  createRoute({ getParentRoute: () => staffLayout, path: "/schedule", component: Schedule }),
  createRoute({ getParentRoute: () => staffLayout, path: "/mailboxes", component: Mailboxes }),
  createRoute({ getParentRoute: () => staffLayout, path: "/auto", component: Automations }),
  createRoute({ getParentRoute: () => staffLayout, path: "/hours", component: Hours }),
  createRoute({ getParentRoute: () => staffLayout, path: "/audit", component: Audit }),
  createRoute({ getParentRoute: () => staffLayout, path: "/perms", component: Permissions }),
  createRoute({ getParentRoute: () => staffLayout, path: "/retention", component: Retention }),
  createRoute({ getParentRoute: () => staffLayout, path: "/trusted-vendors", component: TrustedVendors }),
  createRoute({ getParentRoute: () => staffLayout, path: "/attention", component: Attention }),
  createRoute({ getParentRoute: () => staffLayout, path: "/oncall", component: OnCall }),
  createRoute({ getParentRoute: () => staffLayout, path: "/adminpreview", component: AdminPreview }),
  createRoute({ getParentRoute: () => staffLayout, path: "/flows", component: Flows }),
  createRoute({ getParentRoute: () => staffLayout, path: "/macros", component: Macros }),
  createRoute({ getParentRoute: () => staffLayout, path: "/reports", component: Reports }),
  createRoute({ getParentRoute: () => staffLayout, path: "/parity", component: Parity }),
  createRoute({ getParentRoute: () => staffLayout, path: "/templates", component: Templates }),
  createRoute({ getParentRoute: () => staffLayout, path: "/mls-ops", component: MlsOps }),
  createRoute({ getParentRoute: () => staffLayout, path: "/feed-health", component: FeedHealth }),
  createRoute({ getParentRoute: () => staffLayout, path: "/duplicates", component: Duplicates }),
  createRoute({ getParentRoute: () => staffLayout, path: "/content-studio", component: ContentStudio }),
  createRoute({ getParentRoute: () => staffLayout, path: "/archive", component: Archive }),
]);

/* ------------------------------------------------------------------ *
 * Customer portal (v3) — fully gated, brand-themed, its own chrome.
 * `/portal` is the sign-in screen (no shell); the reading/tickets/projects
 * surfaces live under a pathless shell layout at `/portal/<section>`.
 * ------------------------------------------------------------------ */
const portalRoot = createRoute({
  getParentRoute: () => rootRoute,
  path: "/portal",
  // The provider wraps the whole portal subtree so sign-in and the shell share
  // one session.
  component: () => (
    <PortalProvider>
      <Outlet />
    </PortalProvider>
  ),
});

const portalSignIn = createRoute({ getParentRoute: () => portalRoot, path: "/", component: SignIn });

const portalShell = createRoute({
  getParentRoute: () => portalRoot,
  id: "portalShell",
  component: PortalShell,
  // Gate: an unauthenticated hit on a portal section bounces to sign-in. The
  // verdict is SERVER-authoritative — an httpOnly session cookie the browser can't
  // forge — checked via getSession() (cached briefly, shared with the provider).
  beforeLoad: async () => {
    const s = await portalGetSession();
    if (!s.authed) throw redirect({ to: "/portal" });
  },
});

// Phase 3 delivers the reading experience; these sections are still stubs
// (Phase 4 tickets, Phase 6 projects, later phases for the rest).
const PORTAL_STUBS = ["developers", "community", "downloads", "releases", "status"] as const;
const portalStubRoutes = PORTAL_STUBS.map((seg) =>
  createRoute({ getParentRoute: () => portalShell, path: seg, component: PortalSection }),
);

const portalHome = createRoute({ getParentRoute: () => portalShell, path: "home", component: PortalHome });
const portalGuides = createRoute({
  getParentRoute: () => portalShell,
  path: "guides",
  component: PortalCategory,
  validateSearch: (s: Record<string, unknown>): { cat?: string } => (typeof s.cat === "string" ? { cat: s.cat } : {}),
});
const portalOnboarding = createRoute({ getParentRoute: () => portalShell, path: "onboarding", component: PortalOnboarding });
const portalArticle = createRoute({ getParentRoute: () => portalShell, path: "article/$id", component: PortalArticle });
const portalTickets = createRoute({ getParentRoute: () => portalShell, path: "tickets", component: PortalTickets });
const portalTicket = createRoute({ getParentRoute: () => portalShell, path: "ticket/$id", component: PortalTicket });
const portalNewTicket = createRoute({ getParentRoute: () => portalShell, path: "new-ticket", component: PortalNewTicket });
const portalProjects = createRoute({ getParentRoute: () => portalShell, path: "projects", component: PortalProjects });
const portalProject = createRoute({ getParentRoute: () => portalShell, path: "project/$id", component: PortalProject });
const portalAsk = createRoute({
  getParentRoute: () => portalShell,
  path: "ask",
  component: PortalAsk,
  validateSearch: (s: Record<string, unknown>): { q?: string } => (typeof s.q === "string" ? { q: s.q } : {}),
});

const routeTree = rootRoute.addChildren([
  staffRoutes,
  portalRoot.addChildren([
    portalSignIn,
    portalShell.addChildren([portalHome, portalGuides, portalOnboarding, portalArticle, portalAsk, portalTickets, portalTicket, portalNewTicket, portalProjects, portalProject, ...portalStubRoutes]),
  ]),
]);

export const router = createRouter({ routeTree, defaultPreload: "intent" });

declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }
}
